import FetchHelper from '../helpers/fetch_helper';
import WindowHelper from '../helpers/window_helper';
import ProposalHelper from '../helpers/proposal_helper';
import { WizardSteps } from '../components/proposal/wizard_steps';

const ProposalDispatcher = {
  clearProposal(data) {
    let currentView;
    if (data && data.reload) {
      currentView = this.getStoreVal('view');
      this.setStoreVal('view', { page: 'reloading' });
    }
    this.setStoreVal('proposal', null);
    this.setStoreVal('customer', null);
    this.setStoreVal('eligibility', null);
    this.dispatch({ type: 'clearFinancingMethod' });
    this.setStoreVal('legalDocuments', []);
    this.setStoreVal('prescreenProducts', null);
    this.setStoreVal('propertyInfo', null);
    this.setStoreVal('energyDetails', null);
    this.setStoreVal('financingRequest', null);
    if (data && data.reload) {
      this.setStoreVal('view', currentView);
    }
  },

  async createProposal(data) {
    try {
      const customerResponse = await FetchHelper.fetchJson('customers', {
        body: data,
        version: '/v3',
        method: 'POST',
      });
      let customer = customerResponse.results[0];

      // this.dispatch({ type: 'setRoute', data: `/customer/${customer?.id}/proposal` });
      // this.dispatch({ type: 'setView', data: { subPage: 'customize' } });
      this.dispatch({
        type: 'setProposal',
        data: customer?.proposal,
      });
      this.dispatch({
        type: 'setCustomer',
        data: customer,
      });

      const simpleFinancingMethod = ProposalHelper.determineFinancingMethodToFetch(customer?.proposal);
      const financingMethodId = simpleFinancingMethod.id;

      await Promise.all([
        this.dispatch({
          type: 'getFinancingMethod',
          data: { proposalId: customer?.proposal.id, id: financingMethodId },
        }),
        this.dispatch({ type: 'fetchEnergyDetails', data: customer.id }),
      ]);

      new WizardSteps({
        isGuestUser: false,
        billBeforeAgree: false,
        proposal: this.getStoreVal('proposal'),
        wizardStepsDebug: false,
        userFunctionalityAcls: this.getStoreVal('userFunctionalityAcls'),
        customer,
        financingMethod: this.getStoreVal('financingMethod'),
      })
        .getStep(0)
        .onLoad();

      // this.dispatch({ type: 'goToProposalPage', data: { subPage: 'customize' } });
      return customer;
    } catch (error) {
      console.error('error', error);
      await this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Invalid zip code' } });
      throw error;
    }
  },

  async deleteProposal(proposalId) {
    await FetchHelper.fetchJson(`proposals/${proposalId}`, { method: 'delete' });
  },

  async fetchProposal(proposalId) {
    let proposal;

    if (proposalId === undefined) {
      const view = this.getStoreVal('view');
      proposalId = view.proposalId;
    }

    if (proposalId === undefined) {
      this.dispatch({ type: 'setErrorMessages', data: ['Proposal ID not specified'] });
    } else {
      try {
        const proposalResponse = await FetchHelper.fetchJson(`proposals/${proposalId}`, { method: 'GET' });
        proposal = proposalResponse.results[0];
      } catch (error) {
        this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Something went wrong' } });
      }

      this.dispatch({
        type: 'setProposal',
        data: proposal,
      });
    }
    return proposal;
  },

  async fetchSignature(proposalId) {
    try {
      const signaturesResponse = await FetchHelper.fetchJson(`proposals/${proposalId}/signatures`, { method: 'get' });

      this.setStoreVal('signature', signaturesResponse.results[0]);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to fetch signatures' },
      });
    }
  },

  async financeProposal(id) {
    try {
      await FetchHelper.fetchJson(`proposals/${id}/financing-requests`, { method: 'POST' });
      this.dispatch({ type: 'setMessages', data: ['Proposal successfully submitted for Financing'] });
      this.dispatch({ type: 'goToProposalPage' });
    } catch (error) {
      this.dispatch({ type: 'handleRequestError', data: { error, defaultErrorMessage: 'Something went wrong' } });
    }
  },

  goToProposalPage(data) {
    let proposalId, financingMethodId, subPage, financingRequestId, previousPage;
    if (data) {
      proposalId = data.proposalId;
      financingMethodId = data.financingMethodId;
      previousPage = data.previousPage;
      subPage = data.subPage;
      financingRequestId = data.financingRequestId;
    } else {
      // with the wizard, the default proposal page should be the customize one.
      this.dispatch({ type: 'goToProposalPage', data: { subPage: 'customize' } });
      return;
    }

    if (!proposalId) {
      const proposal = this.getStoreVal('proposal');
      if (proposal?.id) {
        proposalId = proposal.id;
        financingRequestId = proposal.financingRequestId;
      } else {
        const view = this.getStoreVal('view');
        if (view && view.proposalId) {
          proposalId = view.proposalId;
        } else {
          let urlPiece = window.location.href.split('/proposal/');
          if (urlPiece && urlPiece[1]) {
            proposalId = urlPiece[1].split('/')[0];
          }
        }
      }
    }

    if (previousPage) {
      this.setStoreVal('previousPage', previousPage);
    }

    if (!financingMethodId) {
      const financingMethod = this.getStoreVal('financingMethod');
      if (financingMethod) {
        financingMethodId = financingMethod.id;
      }
    }

    let path = `/proposal/${proposalId}`;

    if (financingMethodId) {
      path += `/financing-method/${financingMethodId}`;

      let frSubPages = ['step-documents', 'step-documents-external'];
      let fmSubPages = ['customer', 'address', 'review', 'hand-off', 'eligibilityStatus', 'quickCheckStatus'];

      if (subPage) {
        if (frSubPages.indexOf(subPage) > -1) {
          path = `/proposal/${proposalId}/financing-request/${financingRequestId}/${subPage}`;
        } else if (fmSubPages.indexOf(subPage) > -1) {
          path = `/proposal/${proposalId}/financing-method/${financingMethodId}/${subPage}`;
        } else {
          path += `/${subPage}`;
        }
      }
    }

    this.dispatch({ type: 'setRoute', data: path });
  },

  goToProposalPageForProductSku(sku) {
    this.dispatch({ type: 'goToProposalPage' });
  },

  async previewProposal({ proposalId, financingMethodId }) {
    try {
      const previewPromise = FetchHelper.fetchJson(`proposals/${proposalId}/proposal-documents/bytes`, {
        method: 'POST',
        isBytes: true,
        body: {
          financingMethodId,
        },
      });
      WindowHelper.openFile(previewPromise);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Could not preview proposal' },
      });
    }
  },

  setProposal(proposal) {
    // set isPartnerCreated based on the co id of the guest company (37311)
    if (proposal) {
      proposal.isPartnerCreated = proposal.coid !== 37311;
    }

    this.setStoreVal('proposal', proposal);
  },

  zipCodeSet(data) {
    this.setStoreVal('zipCode', data);
  },

  setDefaultServiceAddressType(data) {
    this.setStoreVal('defaultServiceAddressType', data);
  },
};

export default ProposalDispatcher;
